import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BsPlus, BsX } from 'react-icons/bs'
import { logEvent } from '@firebase/analytics'
import analytics from '../Utils/FirebaseAnalytics'
import { MobileTopBarLink, NewsCard, SkeletonCards, Topics } from '../Components'
import { IS_MOBILE } from '../Contstants'
import { myTopicsService } from '../Services/MyTopicsService'
import { addMyTopic, loadMyTopics, removeMyTopic } from '../Stores/Actions'
import { pluralBs } from '../Utils/Helpers'

/* "Moje teme": follow topics or own keywords, and read a feed of the stories
   that match them. Profile + topics live on the server (api/user_topics.py);
   this browser only keeps the profile's token (Stores/Reducers/MyTopics.js). */
export default function MyTopics() {
  const dispatch = useDispatch()
  const { token, items: myTopics } = useSelector((state) => state.myTopics)
  const { topics } = useSelector((state) => state.news)

  const [activeTopic, setActiveTopic] = useState(null) // user topic id, null = all
  const [news, setNews] = useState([])
  const [loading, setLoading] = useState(false)
  const page = useRef(1)
  const allLoaded = useRef(false)

  const [keywords, setKeywords] = useState('')
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState(null)
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    dispatch(loadMyTopics())
  }, [])

  // Live preview while typing: "N vijesti u zadnjih 7 dana" + sample headlines.
  useEffect(() => {
    setError(null)
    const text = keywords.trim()
    if (text.length < 2) {
      setPreview(null)
      return
    }
    const timer = setTimeout(async () => {
      const response = await myTopicsService.previewKeywords(text)
      if (response.isOk) {
        setPreview(response.data)
      } else {
        setPreview(null)
        setError(response.message)
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [keywords])

  const getNews = async () => {
    if (loading || allLoaded.current || !token) return
    setLoading(true)
    const response = await myTopicsService.getMyNews(token, {
      page: page.current,
      topicId: activeTopic,
    })
    if (response.isOk) {
      const { items, current_page, pages } = response.data
      if (current_page >= pages) allLoaded.current = true
      setNews((prev) => (current_page === 1 ? items : [...prev, ...items]))
      page.current = current_page + 1
    }
    setLoading(false)
  }

  // Reload the feed when the filter or the set of topics changes.
  useEffect(() => {
    page.current = 1
    allLoaded.current = false
    setNews([])
    if (token && myTopics.length > 0) {
      getNews()
    }
  }, [token, activeTopic, myTopics.length])

  // A removed topic can't stay selected.
  useEffect(() => {
    if (activeTopic && !myTopics.find((t) => t.id === activeTopic)) setActiveTopic(null)
  }, [myTopics, activeTopic])

  const observer = useRef()
  const lastNewsElementRef = useCallback(
    (node) => {
      if (news.length < 10) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) getNews()
      })
      if (node) observer.current.observe(node)
    },
    [news.length, activeTopic, token]
  )

  const addKeywords = async (event) => {
    event.preventDefault()
    const text = keywords.trim()
    if (!text || adding) return
    setAdding(true)
    const result = await dispatch(addMyTopic({ keywords: text }))
    setAdding(false)
    if (result.isOk) {
      setKeywords('')
      setPreview(null)
      logEvent(analytics, 'w_keyword_topic_added')
    } else {
      setError(result.message)
    }
  }

  const follow = async (topicId) => {
    await dispatch(addMyTopic({ topic_id: topicId }))
    logEvent(analytics, 'w_topic_followed')
  }

  const followedIds = new Set(myTopics.map((t) => t.topic_id).filter(Boolean))
  const suggestions = (topics ?? [])
    .filter((t) => !followedIds.has(t.id))
    .sort((a, b) => Number(a.is_category) - Number(b.is_category)) // current stories first
    .slice(0, 12)

  return (
    <>
      <MobileTopBarLink />
      <div className="content" style={{ marginTop: IS_MOBILE ? 60 : 0 }}>
        <div className="px-2 my-topics-page">
          <h1 className="my-topics-title">{`Moje teme`}</h1>
          <p className="my-topics-intro">
            {`Pratite priče, ljude, mjesta ili klubove koji vas zanimaju i čitajte samo njih.`}
          </p>

          <form className="my-topics-add" onSubmit={addKeywords}>
            <input
              type="text"
              value={keywords}
              maxLength={60}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="Dodaj temu, npr. Željezničar ili Mostar"
              aria-label="Dodaj temu"
            />
            <button type="submit" disabled={adding || !preview}>
              <BsPlus />
              <span>{`Dodaj`}</span>
            </button>
          </form>
          {error && <div className="my-topics-error">{error}</div>}
          {preview && (
            <div className="my-topics-preview">
              <div className="my-topics-preview-count">
                {preview.articles_7d > 0
                  ? `${preview.articles_7d} ${pluralBs(preview.articles_7d, 'vijest', 'vijesti', 'vijesti')} u zadnjih 7 dana`
                  : `Trenutno nema vijesti s ovim riječima, ali pratit ćemo ih.`}
              </div>
              {preview.sample.slice(0, 3).map((s) => (
                <div key={s.id} className="my-topics-preview-item">
                  <span>{s.portal}</span> {s.title}
                </div>
              ))}
            </div>
          )}

          {myTopics.length > 0 && (
            <div className="my-topics-chips" role="tablist">
              <button
                type="button"
                className={`my-topic-chip ${activeTopic === null ? 'active' : ''}`}
                onClick={() => setActiveTopic(null)}
              >
                {`Sve`}
              </button>
              {myTopics.map((t) => (
                <span key={t.id} className={`my-topic-chip ${activeTopic === t.id ? 'active' : ''}`}>
                  <button type="button" onClick={() => setActiveTopic(t.id)}>
                    {t.label}
                  </button>
                  <button
                    type="button"
                    className="my-topic-remove"
                    aria-label={`Ukloni ${t.label}`}
                    onClick={() => dispatch(removeMyTopic(t.id))}
                  >
                    <BsX />
                  </button>
                </span>
              ))}
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="my-topics-suggestions">
              <div className="my-topics-suggestions-title">{`Prijedlozi`}</div>
              <div>
                {suggestions.map((t) => (
                  <button key={t.id} type="button" className="suggestion-chip" onClick={() => follow(t.id)}>
                    <BsPlus /> {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {myTopics.length === 0 ? (
            <div className="my-topics-empty">
              {`Još ne pratite nijednu temu. Dodajte riječi iznad ili izaberite neki od prijedloga.`}
            </div>
          ) : loading && news.length === 0 ? (
            <SkeletonCards />
          ) : news.length === 0 ? (
            <div className="my-topics-empty">{`Nema novih vijesti za vaše teme.`}</div>
          ) : (
            news.map((item, index) =>
              index === news.length - 1 ? (
                <div id={item.id} ref={lastNewsElementRef} key={item.id}>
                  <NewsCard news={item} includeAd={false} />
                </div>
              ) : (
                <NewsCard key={item.id} news={item} includeAd={false} />
              )
            )
          )}
        </div>
        <div className="right-wrapper pt-5">
          <Topics />
        </div>
      </div>
    </>
  )
}
